if (!customElements.get('new-mega-menu')) {
  class NewMegaMenu extends HTMLElement {
    constructor() {
      super();

      // Init elements
      this.buttons = this.querySelectorAll('.new-mega-menu__nav-button');
      this.links = this.querySelectorAll('.new-mega-menu__nav-link');
      this.modal = this.querySelector('.new-mega-menu__modal');
      this.dropdowns = this.querySelectorAll('.new-mega-menu__dropdown');
      this.header = document.querySelector('sticky-header');
      this.headerFirstChild = this.header?.firstElementChild;
      this.openIndex = null;
      this.imagesWarmed = false;

      this.setupEventListeners();
      this.scheduleImageWarmup();
    }

    // Die Bilder im Dropdown stehen auf loading="lazy", damit sie den ersten
    // Seitenaufbau nicht ausbremsen. Das Menue haengt aber dauerhaft im DOM und
    // ist zu, der Browser wuerde sie also von sich aus nie laden und man saehe
    // sie beim Oeffnen nachpoppen. Deshalb holen wir sie nach, sobald die Seite
    // durch ist und der Hauptthread Luft hat.
    warmImages() {
      if (this.imagesWarmed) return;
      this.imagesWarmed = true;

      this.querySelectorAll('img[loading="lazy"]').forEach((img) => {
        img.loading = 'eager';
      });
    }

    scheduleImageWarmup() {
      // Wer hovert oder hintabbt, oeffnet gleich - dann sofort laden.
      this.buttons.forEach((button) => {
        button.addEventListener('mouseover', () => this.warmImages(), { once: true });
        button.addEventListener('focus', () => this.warmImages(), { once: true });
      });

      const warmWhenIdle = () => {
        // Unter 990px ist das Menue per CSS komplett aus, da braucht niemand die Bilder.
        if (!window.matchMedia('(min-width: 990px)').matches) return;

        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.warmImages(), { timeout: 3000 });
        } else {
          setTimeout(() => this.warmImages(), 1500);
        }
      };

      if (document.readyState === 'complete') {
        warmWhenIdle();
      } else {
        window.addEventListener('load', warmWhenIdle, { once: true });
      }
    }

    setupEventListeners() {
      this.buttons.forEach((button, index) => {
        button.addEventListener('mouseover', () => this.openDropdown(index));

        // Keyboard/click: toggle instead of open, so the same key closes it again
        button.addEventListener('click', (event) => {
          event.preventDefault();
          if (this.openIndex === index) {
            this.closeModal();
          } else {
            this.openDropdown(index);
          }
        });

        button.addEventListener('keydown', (event) => {
          // Down arrow opens and moves into the dropdown
          if (event.key === 'ArrowDown') {
            event.preventDefault();
            this.openDropdown(index);
            this.focusFirstItem(index);
            return;
          }

          // The dropdown lives in a separate container at the end of the DOM,
          // so tabbing off an open trigger would skip past the whole menu.
          // Redirect it into the dropdown the trigger actually controls.
          if (event.key === 'Tab' && !event.shiftKey && this.openIndex === index) {
            const first = this.getDropdownItems(index)[0];
            if (first) {
              event.preventDefault();
              first.focus();
            }
          }
        });
      });

      // Tab handling inside the open dropdown: backwards off the first item
      // returns to the trigger, forwards off the last item closes the menu and
      // continues with the next item in the nav.
      this.modal.addEventListener('keydown', (event) => {
        if (event.key !== 'Tab' || this.openIndex === null) return;

        const items = this.getDropdownItems(this.openIndex);
        if (!items.length) return;

        const trigger = this.buttons[this.openIndex];
        const atFirst = document.activeElement === items[0];
        const atLast = document.activeElement === items[items.length - 1];

        if (event.shiftKey && atFirst) {
          event.preventDefault();
          this.closeModal();
          trigger.focus();
        } else if (!event.shiftKey && atLast) {
          const navItems = this.getNavItems();
          const next = navItems[navItems.indexOf(trigger) + 1];
          this.closeModal();
          if (next) {
            event.preventDefault();
            next.focus();
          }
        }
      });

      // Escape closes and returns focus to the trigger
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && this.openIndex !== null) {
          const trigger = this.buttons[this.openIndex];
          this.closeModal();
          trigger?.focus();
        }
      });

      // Tabbing out of the menu closes it
      this.addEventListener('focusout', () => {
        requestAnimationFrame(() => {
          if (this.openIndex !== null && !this.contains(document.activeElement)) {
            this.closeModal();
          }
        });
      });

      // Close modal if mouse leaves the modal background
      this.modal.addEventListener('mouseleave', (event) => {
        if (!this.isMovingDown(event)) {
          this.closeModal();
        }
      });

      // Close modal if mouse leaves the first child of the header (only if moving up/left/right)
      if (this.headerFirstChild) {
        this.headerFirstChild.addEventListener('mouseleave', (event) => {
          if (!this.isMovingDown(event)) {
            this.closeModal();
          }
        });
      }

      this.dropdowns.forEach((dropdown) => {
        dropdown.addEventListener('mouseleave', () => this.closeModal());
      });

      this.links.forEach((link) => {
        link.addEventListener('mouseleave', () => this.closeModal());
        link.addEventListener('mouseover', () => this.closeModal());
        // Tabbing onto a plain link closes whatever dropdown is open
        link.addEventListener('focus', () => this.closeModal());
      });

      // Close modal when clicking on its background
      this.modal.addEventListener('click', (event) => {
        if (event.target === this.modal) {
          this.closeModal();
        }
      });
    }

    openDropdown(index) {
      this.setModalTopPosition();
      this.modal.setAttribute('data-open', '');
      document.body.style.overflow = 'hidden';

      this.buttons.forEach((b) => {
        b.removeAttribute('data-active');
        b.setAttribute('aria-expanded', 'false');
      });
      this.buttons[index].setAttribute('data-active', '');
      this.buttons[index].setAttribute('aria-expanded', 'true');

      this.dropdowns.forEach((d) => d.removeAttribute('data-show'));
      this.dropdowns[index].setAttribute('data-show', '');

      this.openIndex = index;
    }

    // Focusable items inside one dropdown, in visual order.
    getDropdownItems(index) {
      const dropdown = this.dropdowns[index];
      if (!dropdown) return [];
      return Array.from(dropdown.querySelectorAll('a[href], button:not([disabled])')).filter(
        (el) => el.offsetWidth > 0 || el.offsetHeight > 0
      );
    }

    // Triggers and plain links of the top bar, in tab order.
    getNavItems() {
      return Array.from(this.querySelectorAll('.new-mega-menu__nav-button, .new-mega-menu__nav-link'));
    }

    focusFirstItem(index) {
      this.getDropdownItems(index)[0]?.focus();
    }

    closeModal() {
      this.modal.removeAttribute('data-open');
      document.body.style.overflow = '';
      this.dropdowns.forEach((d) => d.removeAttribute('data-show'));
      this.buttons.forEach((b) => {
        b.removeAttribute('data-active');
        b.setAttribute('aria-expanded', 'false');
      });
      this.openIndex = null;
    }

    setModalTopPosition() {
      const headerRect = this.header.getBoundingClientRect();
      this.modal.style.top = `${headerRect.height - 1 + headerRect.top}px`;
    }

    isMovingDown(event) {
      return event.relatedTarget && event.relatedTarget.closest('.new-mega-menu__dropdown');
    }
  }

  customElements.define('new-mega-menu', NewMegaMenu);
}
